import React, { useState, useEffect } from 'react';
import '../styles/client-testimonials.css';

interface Testimonial {
  id: string;
  name: string;
  caseType: string;
  quote: string;
  rating: number;
  location?: string;
}

interface ClientTestimonialsProps {
  title?: string;
  subtitle?: string;
  description?: string;
  sectionNumber?: string;
  testimonials?: Testimonial[];
  autoPlayInterval?: number;
}

const ClientTestimonials: React.FC<ClientTestimonialsProps> = ({
  title = "Client Testimonials",
  subtitle = "What Our Clients Say",
  description = "Real stories from real people who trusted us with their legal matters.",
  sectionNumber = "0003",
  testimonials = [
    {
      id: "1",
      name: "Sarah Johnson",
      caseType: "Car Accident Case",
      quote: "Muse Legal fought tirelessly for me after my accident. They secured a settlement that covered all my medical bills and then some. I couldn't have asked for better representation.",
      rating: 5,
      location: "Los Angeles, CA"
    },
    {
      id: "2",
      name: "Michael Chen",
      caseType: "Workplace Injury",
      quote: "Professional, compassionate, and incredibly effective. They made a stressful situation manageable and got me the compensation I deserved for my workplace injury.",
      rating: 5,
      location: "San Francisco, CA"
    },
    {
      id: "3",
      name: "Jennifer Martinez",
      caseType: "Slip and Fall",
      quote: "I was hesitant to pursue legal action, but Muse Legal made the entire process smooth and stress-free. Their expertise and dedication resulted in an excellent outcome.",
      rating: 5,
      location: "San Diego, CA"
    },
    {
      id: "4",
      name: "Robert Williams",
      caseType: "Medical Malpractice",
      quote: "After a medical error changed my life, Muse Legal stepped in and held the responsible parties accountable. Their attention to detail and commitment to justice was remarkable.",
      rating: 5,
      location: "Sacramento, CA"
    }
  ],
  autoPlayInterval = 5000
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % testimonials.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length, autoPlayInterval]);

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    setIsAutoPlaying(false);
  };

  const nextSlide = () => {
    setActiveIndex((current) => (current + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setActiveIndex((current) => (current - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  return (
    <div className="client-testimonials-container">
      {/* Header Section */}
      <div className="client-testimonials-header">
        <div className="client-testimonials-subtitle-section">
          <h1 className="client-testimonials-subtitle">{subtitle}</h1>
          <p className="client-testimonials-description">{description}</p>
        </div>
      </div>

      {/* Carousel */}
      <div className="testimonials-carousel">
        <button 
          className="carousel-nav carousel-nav-prev" 
          onClick={prevSlide}
          aria-label="Previous testimonial"
        >
          ‹
        </button>

        <div className="testimonials-track">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`testimonial-card ${index === activeIndex ? 'active' : ''} ${
                index < activeIndex ? 'prev' : index > activeIndex ? 'next' : ''
              }`}
            >
              <div className="testimonial-content">
                <div className="testimonial-quote-mark">"</div>
                <p className="testimonial-quote">{testimonial.quote}</p>
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="star">★</span>
                  ))}
                </div>
                <div className="testimonial-author">
                  <h3 className="testimonial-name">{testimonial.name}</h3>
                  <p className="testimonial-case">{testimonial.caseType}</p>
                  {testimonial.location && (
                    <p className="testimonial-location">{testimonial.location}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button 
          className="carousel-nav carousel-nav-next" 
          onClick={nextSlide}
          aria-label="Next testimonial"
        >
          ›
        </button>
      </div>

      {/* Dots Navigation */}
      <div className="carousel-dots">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${index === activeIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ClientTestimonials;

